import { apiClient } from "@/services/apiClient";

/**
 * A company-level product / service.
 *
 * Field-for-field the same shape as ClientCatalogItem minus `clientId`, so the
 * two can be merged into one invoice-line picker without a mapping layer —
 * see the approved spec "Company product catalog, selectable from an invoice
 * line". Keep them in step: a divergence here is what forces that layer.
 */
export interface Product {
  id: string;
  sku?: string;
  description: string;
  unitPrice: number;
  defaultTaxCategory: string;
  /** Tax rate as a FRACTION (0.22 = 22%). Absent = derive from the category —
   *  every row written before régimen rates existed. */
  defaultTaxRate?: number;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateProductPayload {
  sku?: string;
  description: string;
  unitPrice: number;
  defaultTaxCategory: string;
  /** Fraction, e.g. 0.022. Null clears it. */
  defaultTaxRate?: number | null;
  sortOrder?: number;
}

export type UpdateProductPayload = Partial<
  Omit<CreateProductPayload, "description" | "sku"> & {
    description?: string;
    /** Omit or pass null to clear the code. */
    sku?: string | null;
  }
>;

export async function listProducts(): Promise<Product[]> {
  const { data } = await apiClient.get<{ data: Product[] }>("/products");
  return data.data;
}

export async function createProduct(
  payload: CreateProductPayload
): Promise<Product> {
  const { data } = await apiClient.post<Product>("/products", payload);
  return data;
}

export async function updateProduct(
  productId: string,
  payload: UpdateProductPayload
): Promise<Product> {
  const { data } = await apiClient.patch<Product>(
    `/products/${productId}`,
    payload
  );
  return data;
}

export async function deleteProduct(productId: string): Promise<void> {
  await apiClient.delete(`/products/${productId}`);
}
